export enum WhoQuestionType {
  YesNo = 'YesNo',
  YesNoNA = 'YesNoNA',
  TrueFalse = 'TrueFalse',
  YesNoNone = 'YesNoNone',
  NonAdult = 'NonAdult',
  Surgery = 'Surgery',
  Airway = 'Airway',
  FreeText = 'FreeText'
}

export interface WhoQuestionMapping {
  questionId: string;
  questionType: WhoQuestionType;
  isArray?: boolean; // For questions that return arrays like Q6a, Q7
  dependsOn?: {
    questionId: string;
    showWhen: string[]; // Array of values when this question should be shown
  };
}

export const WHO_QUESTION_MAPPINGS: WhoQuestionMapping[] = [
  // Yes/No questions
  { questionId: '6', questionType: WhoQuestionType.YesNo },
  { questionId: '8', questionType: WhoQuestionType.YesNo },
  { questionId: '9', questionType: WhoQuestionType.YesNo },
  { questionId: '10', questionType: WhoQuestionType.YesNo },
  { questionId: '11', questionType: WhoQuestionType.YesNo },
  { questionId: '12', questionType: WhoQuestionType.YesNo },
  { questionId: '14', questionType: WhoQuestionType.YesNo },
  { questionId: '15', questionType: WhoQuestionType.YesNo },
  { questionId: '16', questionType: WhoQuestionType.YesNo },
  { questionId: '17', questionType: WhoQuestionType.YesNo },
  { questionId: '18', questionType: WhoQuestionType.YesNo },
  { questionId: '22', questionType: WhoQuestionType.YesNo },
  { questionId: '24', questionType: WhoQuestionType.YesNo },
  { questionId: '26', questionType: WhoQuestionType.YesNo },

  // True/False questions
  { questionId: '13', questionType: WhoQuestionType.TrueFalse },
  { questionId: '19', questionType: WhoQuestionType.TrueFalse },
  { questionId: '23', questionType: WhoQuestionType.TrueFalse },
  { questionId: '28', questionType: WhoQuestionType.TrueFalse },

  // Yes/No/NA questions
  { questionId: '20', questionType: WhoQuestionType.YesNoNA },
  { questionId: '21', questionType: WhoQuestionType.YesNoNA },
  { questionId: '25', questionType: WhoQuestionType.YesNoNA },

  // Special question types
  { questionId: '6a', questionType: WhoQuestionType.NonAdult, isArray: true, dependsOn: { questionId: '6', showWhen: ['2'] } },
  { questionId: '7', questionType: WhoQuestionType.Surgery, isArray: true },
  { questionId: '27', questionType: WhoQuestionType.Airway }
];

/**
 * Get the question type for a given question ID
 */
export function getQuestionType(questionId: string): WhoQuestionType {
  const mapping = WHO_QUESTION_MAPPINGS.find(m => m.questionId.toLowerCase() === questionId.toLowerCase());
  return mapping?.questionType ?? WhoQuestionType.YesNo; // Default fallback
}

/**
 * Check if a question returns an array value
 */
export function isArrayQuestion(questionId: string): boolean {
  const mapping = WHO_QUESTION_MAPPINGS.find(m => m.questionId.toLowerCase() === questionId.toLowerCase());
  return mapping?.isArray ?? false;
}

/**
 * Get the WhoChecklist property name for a question ID
 */
export function getWhoChecklistPropertyName(questionId: string): string {
    const propertyName = `q${questionId}Options`;
  return propertyName;
}

/**
 * Check if a question should be shown based on its dependencies
 */
export function shouldShowQuestion(questionId: string, whoChecklist: any): boolean {
  const mapping = WHO_QUESTION_MAPPINGS.find(m => m.questionId.toLowerCase() === questionId.toLowerCase());
  
  // Special logic for question 6a: only filter if question 6 has been answered
  if (questionId.toLowerCase() === '6a') {
    const dependentQuestionPropertyName = getWhoChecklistPropertyName('6');
    const dependentQuestionValue = whoChecklist[dependentQuestionPropertyName];
    
    // If question 6 hasn't been answered yet, show all questions
    if (dependentQuestionValue === null || dependentQuestionValue === undefined) {
      return true;
    }
    
    // Question 6a should only show when question 6 is answered as "No" (value 2)
    // Handle both numeric values and string representations
    const valueToCheck = dependentQuestionValue.toString();
    const shouldShowBasedOnAnswer = mapping?.dependsOn?.showWhen.includes(valueToCheck) ?? false;
    
    return shouldShowBasedOnAnswer;
  }
  
  // For all other questions, show them regardless of dependencies
  // This ensures all questions are visible by default
  return true;
}
